import { useEffect, useState, useCallback, type ElementType, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  Inbox,
  Copy,
  RefreshCw,
  Power,
  Plus,
  Ticket,
  Shield,
  Trash2,
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useBusiness } from "../../contexts/BusinessContext";
import {
  BUSINESS_ROLES,
  PermissionKey,
  STAFF_DEFAULT_PERMISSIONS,
} from "../../lib/businessPermissions";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const DOWN_LINE = "rgba(60,125,87,0.24)";
const BORDER = LINE;

type Role = "owner" | "staff";

const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  staff: "Staff",
};

const ROLE_OPTIONS: Role[] = BUSINESS_ROLES;

const PERMISSIONS: { key: PermissionKey; label: string; description: string }[] = [
  { key: "dashboard", label: "Dashboard", description: "Business overview and spend charts" },
  { key: "invoices", label: "Invoices", description: "Invoice list, editing, preview and export" },
  { key: "scan", label: "Scan", description: "Upload and process invoice files" },
  { key: "suppliers", label: "Suppliers", description: "Supplier database and product history" },
  { key: "supplier_merge", label: "Merge suppliers", description: "Merge duplicate suppliers inside this business" },
  { key: "par_check", label: "Par checks", description: "Run daily checks and view check history" },
  { key: "par_template", label: "Par template", description: "Add, edit and remove par template items" },
  { key: "reports", label: "Reports", description: "Business reports and downloads" },
  { key: "integrations", label: "Integrations", description: "Storage connections and export settings" },
  { key: "team", label: "Team", description: "Team members, invites and access controls" },
  { key: "owner_summary", label: "Cross-business report", description: "Owner summary across owned businesses" },
];

interface PendingRequest {
  request_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  business_id: string;
  business_name: string;
  message: string | null;
  created_at: string;
}

interface ManagedBusinessCode {
  business_id: string;
  business_name: string;
  code_id: string | null;
  code: string | null;
  expires_at: string | null;
}

interface BusinessMember {
  membership_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  role: Role;
  joined_at: string;
}

interface RpcResult {
  success: boolean;
  code?: string;
  code_id?: string;
  error?: string;
}

function Initials({ name, email }: { name?: string; email?: string }) {
  const source = (name || email || "?").trim();
  const parts = source.split(" ");
  const letters = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
      style={{ backgroundColor: ORANGE }}
    >
      {letters.toUpperCase() || "?"}
    </div>
  );
}

function formatDate(value: string | null) {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function rolePillStyle(role: Role) {
  if (role === "owner") return { color: ORANGE };
  return { color: NAVY };
}

export default function TeamPage({ embedded = false }: { embedded?: boolean }) {
  const { activeBusiness, refreshMemberships } = useBusiness();
  const navigate = useNavigate();
  const isOwner = activeBusiness?.role === "owner";

  const [members, setMembers] = useState<BusinessMember[]>([]);
  const [permissions, setPermissions] = useState<Record<string, Record<string, boolean>>>({});
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [codes, setCodes] = useState<ManagedBusinessCode[]>([]);
  const [requestRoles, setRequestRoles] = useState<Record<string, Role>>({});

  const [membersLoading, setMembersLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [codesLoading, setCodesLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [codeActionId, setCodeActionId] = useState<string | null>(null);
  const [memberActionId, setMemberActionId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!activeBusiness) return;
    setMembersLoading(true);
    const { data, error: rpcError } = await supabase.rpc("get_business_members", {
      p_business_id: activeBusiness.id,
    });
    if (rpcError) {
      setError(rpcError.message);
    } else {
      setMembers((data as BusinessMember[]) ?? []);
    }
    setMembersLoading(false);
  }, [activeBusiness]);

  const fetchPermissions = useCallback(async () => {
    if (!activeBusiness || !isOwner) return;
    const { data, error: permissionError } = await supabase
      .from("business_member_permissions")
      .select("user_id, permission_key, allowed")
      .eq("business_id", activeBusiness.id);
    if (permissionError) {
      setError(permissionError.message);
      return;
    }
    const next: Record<string, Record<string, boolean>> = {};
    ((data as { user_id: string; permission_key: string; allowed: boolean }[]) ?? []).forEach((row) => {
      next[row.user_id] = { ...(next[row.user_id] || {}), [row.permission_key]: row.allowed };
    });
    setPermissions(next);
  }, [activeBusiness, isOwner]);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    const { data, error: rpcError } = await supabase.rpc("get_pending_requests_for_owner");
    if (rpcError) {
      setError(rpcError.message);
    } else {
      const next = ((data as PendingRequest[]) ?? []).filter((request) => {
        return !activeBusiness || request.business_id === activeBusiness.id;
      });
      setRequests(next);
      setRequestRoles((prev) => {
        const roles = { ...prev };
        next.forEach((request) => {
          roles[request.request_id] = roles[request.request_id] ?? "staff";
        });
        return roles;
      });
    }
    setRequestsLoading(false);
  }, [activeBusiness]);

  const fetchCodes = useCallback(async () => {
    setCodesLoading(true);
    const { data, error: rpcError } = await supabase.rpc("get_managed_businesses_with_codes");
    if (rpcError) {
      setError(rpcError.message);
    } else {
      const next = ((data as ManagedBusinessCode[]) ?? []).filter((business) => {
        return !activeBusiness || business.business_id === activeBusiness.id;
      });
      setCodes(next);
    }
    setCodesLoading(false);
  }, [activeBusiness]);

  useEffect(() => {
    setError(null);
    fetchMembers();
    fetchPermissions();
    fetchRequests();
    fetchCodes();
  }, [fetchMembers, fetchPermissions, fetchRequests, fetchCodes]);

  function memberPermission(member: BusinessMember, permission: PermissionKey) {
    if (member.role === "owner") return true;
    if (!Object.prototype.hasOwnProperty.call(permissions[member.user_id] || {}, permission)) {
      return STAFF_DEFAULT_PERMISSIONS[permission] === true;
    }
    return permissions[member.user_id]?.[permission] !== false;
  }

  async function handlePermissionChange(member: BusinessMember, permission: PermissionKey, allowed: boolean) {
    if (!activeBusiness || member.role === "owner") return;
    setError(null);
    setMemberActionId(`${member.membership_id}:${permission}`);
    const { error: permissionError } = await supabase
      .from("business_member_permissions")
      .upsert({
        business_id: activeBusiness.id,
        user_id: member.user_id,
        permission_key: permission,
        allowed,
      });
    setMemberActionId(null);
    if (permissionError) {
      setError(permissionError.message);
      return;
    }
    setPermissions((prev) => ({
      ...prev,
      [member.user_id]: {
        ...(prev[member.user_id] || {}),
        [permission]: allowed,
      },
    }));
  }

  async function handleCodeAction(
    business: ManagedBusinessCode,
    action: "generate" | "renew" | "deactivate",
  ) {
    setError(null);
    setCodeActionId(`${action}:${business.business_id}`);

    const rpc =
      action === "generate"
        ? supabase.rpc("generate_invite_code", { p_business_id: business.business_id })
        : action === "renew"
          ? supabase.rpc("renew_invite_code", { p_code_id: business.code_id })
          : supabase.rpc("deactivate_invite_code", { p_code_id: business.code_id });

    const { data, error: rpcError } = await rpc;
    setCodeActionId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as RpcResult;
    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      return;
    }

    await fetchCodes();
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(null), 1600);
    } catch {
      setError("Could not copy the code. Select it and copy it manually.");
    }
  }

  async function handleRequestAction(requestId: string, action: "approve" | "reject") {
    setError(null);
    setActionId(requestId);

    const params =
      action === "approve"
        ? { p_request_id: requestId, p_role: requestRoles[requestId] ?? "staff" }
        : { p_request_id: requestId };

    const { data, error: rpcError } = await supabase.rpc(
      action === "approve" ? "approve_join_request" : "reject_join_request",
      params,
    );

    setActionId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as RpcResult;
    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      return;
    }

    setRequests((prev) => prev.filter((request) => request.request_id !== requestId));
    await fetchMembers();
  }

  async function handleRoleChange(member: BusinessMember, role: Role) {
    setError(null);
    setMemberActionId(member.membership_id);

    const { data, error: rpcError } = await supabase.rpc("update_member_role", {
      p_membership_id: member.membership_id,
      p_role: role,
    });

    setMemberActionId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as RpcResult;
    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      return;
    }

    await fetchMembers();
    await refreshMemberships();
  }

  async function handleRemoveMember(member: BusinessMember) {
    if (confirmRemoveId !== member.membership_id) {
      setConfirmRemoveId(member.membership_id);
      window.setTimeout(() => {
        setConfirmRemoveId((current) =>
          current === member.membership_id ? null : current
        );
      }, 4000);
      return;
    }

    setError(null);
    setMemberActionId(member.membership_id);
    setConfirmRemoveId(null);

    const { data, error: rpcError } = await supabase.rpc("remove_business_member", {
      p_membership_id: member.membership_id,
    });

    setMemberActionId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const result = data as RpcResult;
    if (!result.success) {
      setError(result.error ?? "Something went wrong.");
      return;
    }

    await fetchMembers();
    await refreshMemberships();
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{
        backgroundColor: embedded ? "transparent" : CANVAS,
        fontFamily: "var(--font-ui)",
      }}
    >
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="mb-2">
            {!embedded && (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/console-settings")}
                  className="flex items-center gap-2 text-sm mb-5 transition-opacity hover:opacity-70"
                  style={{ color: NAVY, opacity: 0.55 }}
                >
                  <ArrowLeft size={16} />
                  Back to settings
                </button>
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: ACCENT_WEAK, color: ORANGE, border: `1px solid ${ACCENT_LINE}` }}
                  >
                    <Users size={22} />
                  </div>
                  <div>
                    <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>
                      Team Preferences
                    </h1>
                    <p className="text-sm mt-1" style={{ color: MUTED }}>
                      Manage team members, invitation codes, access requests, and workspace permissions.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm"
              style={{
                color: UP,
                backgroundColor: UP_WEAK,
                border: `1px solid ${UP_LINE}`,
              }}
            >
              {error}
            </div>
          )}

          <section>
            <div className="mb-3 flex items-center gap-2">
              <Shield size={17} style={{ color: MUTED }} />
              <h2 className="text-sm font-bold" style={{ color: NAVY }}>
                Organisation members
              </h2>
            </div>

            {membersLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={22} className="animate-spin" style={{ color: ORANGE }} />
              </div>
            ) : members.length === 0 ? (
              <EmptyState title="No members found" body="Members will appear here." />
            ) : (
              <Panel>
                {members.map((member, idx) => {
                  const busy = memberActionId === member.membership_id;
                  const confirmingRemove = confirmRemoveId === member.membership_id;
                  return (
                    <div
                      key={member.membership_id}
                      className="flex items-center gap-4 px-5 py-4 flex-wrap"
                      style={{ borderTop: idx > 0 ? `1px solid ${LINE}` : undefined }}
                    >
                      <Initials name={member.user_name} email={member.user_email} />
                      <div className="flex-1 min-w-56">
                        <p className="text-sm font-semibold" style={{ color: NAVY }}>
                          {member.user_name || "Unknown"}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                          {member.user_email} · Joined {formatDate(member.joined_at)}
                        </p>
                      </div>

                      {isOwner ? (
                        <div className="flex items-center gap-2">
                          {busy && <Loader2 size={13} className="animate-spin" style={{ color: ORANGE }} />}
                          <select
                            value={member.role}
                            disabled={busy}
                            onChange={(event) => handleRoleChange(member, event.target.value as Role)}
                            className="rounded-lg px-3 py-2 text-xs font-semibold outline-none"
                            style={{
                              color: NAVY,
                              backgroundColor: SURFACE,
                              border: `1.5px solid ${LINE}`,
                            }}
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member)}
                            disabled={busy}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-40"
                            style={{
                              color: confirmingRemove ? "white" : UP,
                              backgroundColor: confirmingRemove ? UP : SURFACE,
                              border: `1px solid ${confirmingRemove ? UP : UP_LINE}`,
                            }}
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            {confirmingRemove ? "Are you sure?" : "Remove"}
                          </button>
                        </div>
                      ) : (
                        <span
                          className="text-xs font-semibold"
                          style={rolePillStyle(member.role)}
                        >
                          {ROLE_LABELS[member.role]}
                        </span>
                      )}
                    </div>
                  );
                })}
              </Panel>
            )}
          </section>

          {isOwner && (
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Ticket size={17} style={{ color: MUTED }} />
                  <h2 className="text-sm font-bold" style={{ color: NAVY }}>
                    Invitation codes
                  </h2>
                </div>
                <p className="text-xs" style={{ color: MUTED }}>
                  Codes create pending requests
                </p>
              </div>

              {codesLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 size={22} className="animate-spin" style={{ color: ORANGE }} />
                </div>
              ) : codes.length === 0 ? (
                <EmptyState title="No owned businesses" body="Only owners can create invitation codes." />
              ) : (
                <Panel>
                  {codes.map((business, idx) => {
                    const hasCode = Boolean(business.code && business.code_id);
                    const busy = codeActionId?.endsWith(business.business_id);
                    return (
                      <div
                        key={business.business_id}
                        className="flex items-center gap-4 px-5 py-4 flex-wrap"
                        style={{ borderTop: idx > 0 ? `1px solid ${LINE}` : undefined }}
                      >
                        <div className="flex-1 min-w-56">
                          <p className="text-sm font-semibold" style={{ color: NAVY }}>
                            {business.business_name}
                          </p>
                          <p className="text-xs mt-1" style={{ color: MUTED }}>
                            {hasCode ? `Expires ${formatDate(business.expires_at)}` : "No active invitation code"}
                          </p>
                        </div>

                        {hasCode && (
                          <button
                            onClick={() => copyCode(business.code!)}
                            className="font-mono text-sm font-bold px-3 py-2 rounded-lg flex items-center gap-2"
                            style={{
                              color: NAVY,
                              backgroundColor: RAISED,
                              border: `1px solid ${LINE}`,
                            }}
                          >
                            {business.code}
                            <Copy size={13} style={{ color: ORANGE }} />
                            {copiedCode === business.code && (
                              <span className="text-[10px] font-sans" style={{ color: DOWN }}>
                                Copied
                              </span>
                            )}
                          </button>
                        )}

                        <div className="flex items-center gap-2">
                          {hasCode ? (
                            <>
                              <AccessButton
                                label="Renew"
                                icon={busy && codeActionId?.startsWith("renew") ? Loader2 : RefreshCw}
                                disabled={busy}
                                onClick={() => handleCodeAction(business, "renew")}
                              />
                              <AccessButton
                                label="Deactivate"
                                icon={busy && codeActionId?.startsWith("deactivate") ? Loader2 : Power}
                                danger
                                disabled={busy}
                                onClick={() => handleCodeAction(business, "deactivate")}
                              />
                            </>
                          ) : (
                            <button
                              onClick={() => handleCodeAction(business, "generate")}
                              disabled={busy}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-40"
                              style={{
                                backgroundColor: ORANGE,
                                boxShadow: ACCENT_SHADOW,
                              }}
                            >
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                              Generate code
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              )}
            </section>
          )}

          {isOwner && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Users size={17} style={{ color: MUTED }} />
                <h2 className="text-sm font-bold" style={{ color: NAVY }}>
                  Pending requests
                </h2>
              </div>

              {requestsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={24} className="animate-spin" style={{ color: ORANGE }} />
                </div>
              ) : requests.length === 0 ? (
                <EmptyState title="No pending requests" body="Requests for this business will appear here." />
              ) : (
                <Panel>
                  {requests.map((request, idx) => {
                    const busy = actionId === request.request_id;
                    return (
                      <div
                        key={request.request_id}
                        className="flex items-start gap-4 px-5 py-4"
                        style={{ borderTop: idx > 0 ? `1px solid ${LINE}` : undefined }}
                      >
                        <Initials name={request.user_name} email={request.user_email} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <p className="text-sm font-semibold" style={{ color: NAVY }}>
                              {request.user_name || "Unknown"}
                            </p>
                            <span className="text-xs" style={{ color: MUTED }}>
                              {request.user_email}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span
                              className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: ACCENT_WEAK, color: ORANGE, border: `1px solid ${ACCENT_LINE}` }}
                            >
                              {request.business_name}
                            </span>
                            <span className="text-[11px]" style={{ color: MUTED }}>
                              {formatDate(request.created_at)}
                            </span>
                          </div>

                          {request.message && (
                            <p
                              className="text-xs mt-2 leading-relaxed"
                              style={{
                                color: MUTED,
                                backgroundColor: RAISED,
                                borderRadius: 6,
                                padding: "6px 10px",
                                border: `1px solid ${LINE}`,
                              }}
                            >
                              "{request.message}"
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5 flex-wrap justify-end">
                          <select
                            value={requestRoles[request.request_id] ?? "staff"}
                            disabled={busy}
                            onChange={(event) =>
                              setRequestRoles((prev) => ({
                                ...prev,
                                [request.request_id]: event.target.value as Role,
                              }))
                            }
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold outline-none"
                            style={{
                              color: NAVY,
                              backgroundColor: SURFACE,
                              border: `1.5px solid ${LINE}`,
                            }}
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => handleRequestAction(request.request_id, "approve")}
                            disabled={busy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                            style={{
                              backgroundColor: DOWN_WEAK,
                              color: DOWN,
                              border: `1px solid ${DOWN_LINE}`,
                            }}
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                            Approve
                          </button>

                          <button
                            onClick={() => handleRequestAction(request.request_id, "reject")}
                            disabled={busy}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                            style={{
                              backgroundColor: UP_WEAK,
                              color: UP,
                              border: `1px solid ${UP_LINE}`,
                            }}
                          >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                            Reject
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              )}
            </section>
          )}

          {isOwner && (
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Shield size={17} style={{ color: MUTED }} />
                  <h2 className="text-sm font-bold" style={{ color: NAVY }}>
                    Function access
                  </h2>
                </div>
                <p className="text-xs" style={{ color: MUTED }}>
                  Owners always keep full access
                </p>
              </div>
              <Panel>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-sm min-w-[760px]">
                    <colgroup>
                      <col className="w-[46%]" />
                      {members.map((member) => (
                        <col key={`permission-col-${member.membership_id}`} />
                      ))}
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
                          Function
                        </th>
                        {members.map((member) => (
                          <th key={`permission-head-${member.membership_id}`} className="px-3 py-3 text-center align-middle text-[10px] font-semibold uppercase" style={{ color: MUTED }}>
                            <span className="mx-auto block max-w-[9rem] truncate normal-case text-xs tracking-normal opacity-100">
                              {member.user_name || member.user_email}
                            </span>
                            <span className="mx-auto mt-0.5 block max-w-[9rem] truncate normal-case tracking-normal opacity-60">
                              {ROLE_LABELS[member.role]}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSIONS.map((permission, rowIndex) => (
                        <tr key={permission.key} style={{ borderTop: rowIndex > 0 ? `1px solid ${LINE}` : undefined }}>
                          <td className="px-4 py-3">
                            <p className="text-xs font-bold" style={{ color: NAVY }}>{permission.label}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>{permission.description}</p>
                          </td>
                          {members.map((member) => {
                            const allowed = memberPermission(member, permission.key);
                            const disabled = member.role === "owner" || memberActionId === `${member.membership_id}:${permission.key}`;
                            return (
                              <td key={`${permission.key}-${member.membership_id}`} className="px-3 py-3 text-center align-middle">
                                <input
                                  type="checkbox"
                                  checked={allowed}
                                  disabled={disabled}
                                  className="mx-auto block"
                                  onChange={(event) => handlePermissionChange(member, permission.key, event.target.checked)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: SURFACE,
        border: `1px solid ${LINE}`,
        boxShadow: SHADOW,
      }}
    >
      {children}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center py-14 text-center rounded-xl"
      style={{
        backgroundColor: SURFACE,
        border: `1px solid ${LINE}`,
        boxShadow: SHADOW,
      }}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: RAISED, border: `1px solid ${LINE}` }}
      >
        <Inbox size={24} style={{ color: MUTED }} />
      </div>
      <p className="text-sm font-semibold" style={{ color: NAVY }}>
        {title}
      </p>
      <p className="text-xs mt-1" style={{ color: MUTED }}>
        {body}
      </p>
    </div>
  );
}

function AccessButton({
  label,
  icon: Icon,
  onClick,
  disabled,
  danger,
}: {
  label: string;
  icon: ElementType;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-40"
      style={
        danger
          ? {
              color: UP,
              backgroundColor: UP_WEAK,
              border: `1px solid ${UP_LINE}`,
            }
          : {
              color: NAVY,
              backgroundColor: CHIP,
              border: `1px solid ${LINE}`,
            }
      }
    >
      <Icon size={12} className={disabled ? "animate-spin" : undefined} />
      {label}
    </button>
  );
}
